import {
  Component,
  type ErrorInfo,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { raisedPanelSx } from '@/shared/ui/panelShadowStyles';
import { s } from '@/theme/tokens/spacing';
import { Box, Inline, Stack, StatusBanner, Surface, Text } from '@/ui';
import { Button } from '@/ui/primitives';

type RouteErrorBoundaryState = { hasError: boolean; message?: string };
const fallbackContentTextSx = { fontSize: '15px' };

type RouteContext = {
  path: string;
  routeKey?: string;
  organisationId: string | null;
  projectId: string | null;
};

type RouteErrorBoundaryInnerProps = {
  children: ReactNode;
  context: RouteContext;
  onReset: () => void;
};

class RouteErrorBoundaryInner extends Component<
  RouteErrorBoundaryInnerProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // UI fallback is handled by getDerivedStateFromError.
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { context, onReset } = this.props;
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <Box
          sx={{
            width: 'fit-content',
            maxWidth: 'min(100%, 56rem)',
            px: s('lg'),
            py: s('lg'),
            boxSizing: 'border-box',
          }}
        >
          <Surface padding="lg" sx={[raisedPanelSx, { width: 'fit-content', maxWidth: '100%' }]}>
            <Stack gap="md" align="center" sx={{ width: 'fit-content', maxWidth: '100%' }}>
              <StatusBanner
                tone="red"
                message="This page hit an error. Try again, or reload if it persists."
                sx={{ width: 'fit-content', maxWidth: '100%' }}
              />
              {this.state.message ? (
                <Text role="body" color="textSecondary">
                  {this.state.message}
                </Text>
              ) : null}
              <Stack gap="sm" align="center">
                {context.routeKey ? (
                  <Text role="caption" color="textSecondary" sx={fallbackContentTextSx}>
                    Page: {context.routeKey}
                  </Text>
                ) : null}
                <Text role="caption" color="textSecondary" sx={fallbackContentTextSx}>
                  Route: {context.path}
                </Text>
                <Text role="caption" color="textSecondary" sx={fallbackContentTextSx}>
                  Org: {context.organisationId ?? 'personal'} · Project:{' '}
                  {context.projectId ?? 'none'}
                </Text>
              </Stack>
              <Inline
                gap="md"
                align="center"
                justify="center"
                sx={{ width: 'fit-content', maxWidth: '100%', marginTop: s('sm') }}
              >
                <Button variant="contained" onClick={onReset}>
                  Retry
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  Reload
                </Button>
              </Inline>
            </Stack>
          </Surface>
        </Box>
      </Box>
    );
  }
}

export const RouteErrorBoundary = ({
  children,
  routeKey,
}: {
  children: ReactNode;
  routeKey?: string;
}): ReactNode => {
  const location = useLocation();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [resetKey, setResetKey] = useState(0);
  const lastRouteKeyRef = useRef(routeKey);
  const lastPathRef = useRef(location.pathname);

  useEffect(() => {
    if (lastPathRef.current !== location.pathname || lastRouteKeyRef.current !== routeKey) {
      lastPathRef.current = location.pathname;
      lastRouteKeyRef.current = routeKey;
      setResetKey((prev) => prev + 1);
    }
  }, [location.pathname, routeKey]);
  const context = useMemo<RouteContext>(
    () => ({
      path: location.pathname,
      routeKey,
      organisationId: selectedOrganisationId ?? null,
      projectId: selectedProjectId ?? null,
    }),
    [location.pathname, routeKey, selectedOrganisationId, selectedProjectId],
  );

  return (
    <RouteErrorBoundaryInner
      key={resetKey}
      context={context}
      onReset={() => {
        setResetKey((prev) => prev + 1);
      }}
    >
      {children}
    </RouteErrorBoundaryInner>
  );
};
